import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  profile,
  experience,
  journal,
  protocols,
  standards,
  languages,
  selectedWork,
  sideProjects,
  protocolWork,
  whitepaperPlate,
  paper,
  heroVideo,
} from "../data";
import Mark from "../components/Mark";
import Schematic from "../components/Schematic";
import Reader from "../components/Reader";
import Seo from "../components/Seo";

const clean = (text) => text.replace(/[—–]/g, "-");
const external = (href) =>
  href.startsWith("http") ? { target: "_blank", rel: "noreferrer" } : {};
const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));

function DisclosureLabel({ closed = "Details", open = "Close" }) {
  return (
    <span className="disclosure-label">
      <span className="when-closed">{closed}</span>
      <span className="when-open">{open}</span>
    </span>
  );
}
function SrOnly({ children }) {
  return <span className="sr-only">{children}</span>;
}

const NAV = [
  ["#work", "Work"],
  ["#networks", "Networks"],
  ["#experience", "Experience"],
  ["#projects", "Projects"],
  ["#journal", "Writing"],
  ["#contact", "Contact"],
];

function Navigation({ compact }) {
  const [open, setOpen] = useState(false);
  const toggle = useRef(null);
  useEffect(() => {
    const escape = (e) => {
      if (e.key === "Escape" && open) {
        setOpen(false);
        toggle.current?.focus();
      }
    };
    document.addEventListener("keydown", escape);
    return () => document.removeEventListener("keydown", escape);
  }, [open]);
  return (
    <header className={compact || open ? "site-header is-compact" : "site-header"}>
      <div className="shell nav-inner">
        <a href="#home" className="wordmark" aria-label="Fredrick Waihenya, home">
          Fredrick Waihenya
        </a>
        <button
          ref={toggle}
          className="menu-toggle"
          aria-expanded={open}
          aria-controls="primary-navigation"
          onClick={() => setOpen(!open)}
        >
          {open ? "Close" : "Menu"}
        </button>
        <nav
          id="primary-navigation"
          aria-label="Main navigation"
          className={open ? "nav-links is-open" : "nav-links"}
        >
          {NAV.map(([href, label]) => (
            <a key={href} href={href} onClick={() => setOpen(false)}>
              {label}
            </a>
          ))}
        </nav>
      </div>
    </header>
  );
}

function Hero({ mediaRef }) {
  return (
    <section className="hero" id="home" aria-labelledby="hero-title">
      <div className="hero-media" ref={mediaRef} aria-hidden="true">
        {heroVideo.mp4 && !prefersReducedMotion() ? (
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="/images/bunny-financial-world.jpg"
            width="1672"
            height="941"
          >
            {heroVideo.webm && <source src={heroVideo.webm} type="video/webm" />}
            <source src={heroVideo.mp4} type="video/mp4" />
          </video>
        ) : (
          <picture>
            <source
              media="(max-width: 767px)"
              srcSet="/images/bunny-financial-world-1000.jpg"
            />
            <img
              src="/images/bunny-financial-world.jpg"
              alt=""
              width="1672"
              height="941"
              fetchpriority="high"
              decoding="async"
            />
          </picture>
        )}
      </div>
      <div className="hero-shade" aria-hidden="true" />
      <div className="hero-copy shell">
        <p className="eyebrow hero-eyebrow">
          <span>Co-Founder &amp; CTO, Sereel</span>{" "}
          <span>Blockchain infrastructure engineer</span>
        </p>
        <h1 id="hero-title" className="display hero-title">
          <span className="line">
            <span>Bunny</span>
          </span>
        </h1>
        <p className="hero-dek">
          I've spent years building custody, tokenization, and settlement
          systems for institutions that move real assets onchain.
        </p>
        <div className="hero-links">
          <a className="text-link" href={profile.calendly} {...external(profile.calendly)}>
            Book a call
          </a>
        </div>
      </div>
    </section>
  );
}

// Statement that brightens word by word as the chapter scrolls in.
function ScrollText({ text, className }) {
  const words = text.split(" ");
  return (
    <p className={`scroll-text ${className || ""}`} data-scroll-text>
      {words.map((w, i) => (
        <span key={i} style={{ "--k": i / words.length }}>
          {w}
          {i < words.length - 1 ? " " : ""}
        </span>
      ))}
    </p>
  );
}

function Chapter({ id, number, title, titleId, aside, children, tone, before }) {
  return (
    <section
      className={tone ? `chapter chapter-${tone}` : "chapter"}
      id={id}
      aria-labelledby={titleId}
    >
      {before}
      <div className="shell chapter-grid">
        <div className="chapter-aside">
          <p className="eyebrow" data-reveal>
            {number}
          </p>
          <h2 id={titleId} className="display" data-reveal style={{ "--i": 1 }}>
            <SrOnly>{number.replace(/^\d+ \/ /, "")}: </SrOnly>
            {title}
          </h2>
          {aside}
        </div>
        <div className="chapter-body">{children}</div>
      </div>
    </section>
  );
}

function Work({ onOpenPaper }) {
  const openPaper = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    e.preventDefault();
    onOpenPaper();
  };
  return (
    <Chapter
      id="work"
      number="01 / Selected work"
      title="What I build."
      titleId="work-title"
      tone="ink"
      aside={
        <div className="chapter-note" data-reveal style={{ "--i": 2 }}>
          <ScrollText text="Most of my work comes down to four things: securing institutional funds, keeping systems interoperable, letting assets reach markets beyond where they were issued, and giving software agents a safe way to act." />
        </div>
      }
    >
      <ol className="records">
        {selectedWork.map((w, i) => (
          <li className="record" key={w.id} id={`work-${w.id}`}>
            <div className="record-inner" data-reveal>
              {w.image && (
                <figure className="record-banner plate" data-plate>
                  <span className="plate-frame">
                    <img src={w.image.src} alt={w.image.alt} width="1600" height="500" loading="lazy" decoding="async" />
                    <PlateMarks />
                  </span>
                  <figcaption className="eyebrow plate-caption">{w.image.caption}</figcaption>
                </figure>
              )}
              <div className="record-head">
                <h3 className="display">{w.name}</h3>
                {w.subhead && <p className="record-subhead">{w.subhead}</p>}
              </div>
              <div className="record-body">
                <p className="record-text">{w.contribution}</p>
                {(w.status || w.outcome) && (
                  <p className="record-outcome">
                    {w.status && <span className="record-status">{w.status}</span>}
                    {w.outcome}
                  </p>
                )}
                {w.id === "defi" ? (
                  <div className="record-figures">
                    <Schematic id={w.id} />
                    <a className="record-plate plate" href={whitepaperPlate.href} onClick={openPaper} data-plate aria-label="Preview the whitepaper">
                      <span className="plate-frame">
                        <img src={whitepaperPlate.img} alt={whitepaperPlate.alt} width="989" height="1236" loading="lazy" decoding="async" />
                        <PlateMarks />
                      </span>
                      <span className="eyebrow plate-caption">{whitepaperPlate.caption}</span>
                    </a>
                  </div>
                ) : (
                  <Schematic id={w.id} />
                )}
                <ul className="tags" aria-label="Built with">
                  {w.stack.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
                <div className="record-actions">
                  {w.href && w.evidence && (
                    <a
                      className="text-link"
                      href={w.href}
                      onClick={w.id === "defi" ? openPaper : undefined}
                      {...(w.id === "defi" ? {} : external(w.href))}
                    >
                      {w.evidence}
                    </a>
                  )}
                  <details className="record-details">
                    <summary aria-label={`Details: ${w.name}`}>
                      <DisclosureLabel />
                    </summary>
                    <div className="details-body">
                      <p>{w.description}</p>
                      <dl>
                        {w.technologies.map(([name, description]) => (
                          <div key={name}>
                            <dt>{name}</dt>
                            <dd>{description}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  </details>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ol>
      <div className="protocol-work" data-reveal>
        <p className="eyebrow">Protocol experience</p>
        {protocolWork.map((p) => (
          <dl key={p.name}>
            <dt>
              {p.icon && <img src={p.icon} alt="" width="20" height="20" loading="lazy" decoding="async" />}
              {p.name}
            </dt>
            <dd>{p.text}</dd>
          </dl>
        ))}
      </div>
    </Chapter>
  );
}

function Networks() {
  return (
    <Chapter
      id="networks"
      number="02 / Networks"
      title="Where it runs."
      titleId="networks-title"
      aside={
        <dl className="index-rows" data-reveal style={{ "--i": 2 }}>
          <div>
            <dt>Standards</dt>
            <dd>{standards.join(", ")}</dd>
          </div>
          <div>
            <dt>Languages</dt>
            <dd>{languages.join(", ")}</dd>
          </div>
        </dl>
      }
    >
      <ul className="ledger" aria-label="Networks">
        {profile.networks.map((n, i) => (
          <li key={n.name} data-reveal style={{ "--i": i }}>
            <Mark name={n.mark} size={30} className="ledger-mark" />
            <span className="ledger-name">{n.name}</span>
            <span className="ledger-note">{n.highlight}</span>
          </li>
        ))}
      </ul>
      <div className="protocol-row" data-reveal>
        <p className="eyebrow">Protocols</p>
        <ul aria-label="Protocols">
          {protocols.map((p) => (
            <li key={p.name}>
              <img src={p.icon} alt="" width="22" height="22" loading="lazy" decoding="async" />
              <span>{p.name}</span>
            </li>
          ))}
        </ul>
      </div>
    </Chapter>
  );
}

function PlateMarks() {
  return (
    <svg className="plate-marks" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
      <path d="M0 6 H12 M6 0 V12 M88 0 V12 M100 6 H88 M0 94 H12 M6 100 V88 M88 100 V88 M100 94 H88" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

function Projects() {
  const plateLabels = ["I", "II", "III", "IV", "V"];

  return (
    <section className="chapter chapter-wide" id="projects" aria-labelledby="projects-title">
      <div className="shell">
        <div className="wide-heading">
          <div>
            <p className="eyebrow" data-reveal>
              04 / Side projects
            </p>
            <h2 id="projects-title" className="display" data-reveal style={{ "--i": 1 }}>
              <SrOnly>Side projects: </SrOnly>Also built.
            </h2>
          </div>
          <p className="chapter-note" data-reveal style={{ "--i": 2 }}>
            Open-source systems for governed AI context, secure agent access,
            and infrastructure observability.
          </p>
        </div>
        <div className="project-grid" data-cursor-grid>
          {sideProjects.map((p, i) => (
            <a
              className={`project-card plate${p.featured ? " project-card-featured" : ""}`}
              key={p.id}
              href={p.url}
              {...external(p.url)}
              data-plate
            >
              <span className="project-media plate-frame">
                <img src={p.img} alt={p.alt} width="1400" height="933" loading="lazy" decoding="async" />
                <PlateMarks />
              </span>
              <span className="project-cursor" aria-hidden="true">
                {p.action}
              </span>
              <span className="project-copy" data-reveal style={{ "--i": i }}>
                <span className="eyebrow">
                  Plate {plateLabels[i] || i + 1} · {p.kind}
                </span>
                <span className="display project-title">{p.title}</span>
                <span className="project-text">{p.text}</span>
                <span className="project-action">{p.action}</span>
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function Experience() {
  return (
    <Chapter
      id="experience"
      number="03 / Experience"
      title="Where I've worked."
      titleId="experience-title"
      tone="surface"
      aside={
        <p className="chapter-note" data-reveal style={{ "--i": 2 }}>
          Where I've done this work: custody, data infrastructure, and fintech engineering since 2021.
        </p>
      }
    >
      <div className="career-records">
        {experience.map((role, i) => (
          <details className="career-record" key={role.company} data-reveal style={{ "--i": i }}>
            <summary aria-label={`Details: ${role.company}`}>
              <span className="career-company">
                {role.company}
                <small>{role.title}</small>
              </span>
              <span className="career-period">{clean(role.range)}</span>
              <DisclosureLabel />
            </summary>
            <div className="details-body">
              <ul>
                {role.bullets.map((b, j) => (
                  <li key={j}>
                    {typeof b === "string" ? (
                      clean(b)
                    ) : (
                      <>
                        {b.url ? (
                          <a href={b.url} {...external(b.url)}>
                            {b.label}
                          </a>
                        ) : (
                          <strong>{b.label}</strong>
                        )}
                        : {clean(b.text)}
                      </>
                    )}
                  </li>
                ))}
              </ul>
              {role.url && (
                <a className="text-link" href={role.url} {...external(role.url)}>
                  {role.company}
                </a>
              )}
            </div>
          </details>
        ))}
      </div>
    </Chapter>
  );
}

function Journal() {
  return (
    <Chapter
      id="journal"
      number="05 / Writing"
      title="Notes."
      titleId="journal-title"
      tone="surface"
      aside={
        <p className="chapter-note" data-reveal style={{ "--i": 2 }}>
          Notes on tokenization standards, institutional privacy, and owning your own AI context.
        </p>
      }
    >
      <div className="journal-index">
        {[journal[1], journal[2], journal[0]].map((entry, i) => (
          <Link
            key={entry.slug}
            className="journal-record"
            to={`/journal/${entry.slug}`}
            data-reveal
            style={{ "--i": i }}
          >
            <span className="journal-meta">
              {entry.date} · {entry.readTime}
            </span>
            <h3>{entry.title}</h3>
          </Link>
        ))}
      </div>
    </Chapter>
  );
}

function LinkedInIcon() {
  return (
    <svg className="social-icon" aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.06 2.06 0 1 1 0-4.13 2.06 2.06 0 0 1 0 4.13zM7.12 20.45H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z" />
    </svg>
  );
}

function Contact() {
  return (
    <footer className="contact" id="contact">
      <div className="shell">
        <p className="eyebrow" data-reveal>
          Contact
        </p>
        <p className="contact-name display" data-reveal style={{ "--i": 1 }}>
          Fredrick Waihenya
        </p>
        <a
          className="contact-email display"
          href={`mailto:${profile.email}`}
          data-reveal
          style={{ "--i": 2 }}
        >
          {profile.email}
        </a>
        <p className="contact-note" data-reveal style={{ "--i": 3 }}>
          Open to selected consulting and part-time engineering work in custody,
          tokenization, and settlement.
        </p>
        <div className="footer-row">
          <span className="footer-bio">
            Bunny
            <span className="footer-alias">
              Co-Founder &amp; CTO at Sereel, blockchain infrastructure engineer for
              institutional tokenization, custody, and settlement.
            </span>
          </span>
          <div>
            <a href="https://www.sereel.com/" target="_blank" rel="noreferrer">
              Sereel
            </a>
            {profile.socials.map((s) => (
              <a
                key={s.name}
                href={s.url}
                target="_blank"
                rel="me noreferrer"
                aria-label={s.name === "Twitter" ? "X, formerly Twitter" : undefined}
              >
                {s.name === "LinkedIn" && <LinkedInIcon />}
                {s.name === "Twitter" ? "X" : s.name}
              </a>
            ))}
          </div>
          <span>© {new Date().getFullYear()}</span>
        </div>
      </div>
    </footer>
  );
}

export default function PortfolioHome() {
  const mediaRef = useRef(null);
  const [compact, setCompact] = useState(false);
  const [paperOpen, setPaperOpen] = useState(false);

  // Header wordmark appears once the headline has scrolled away.
  useEffect(() => {
    const title = document.getElementById("hero-title");
    if (!title || !("IntersectionObserver" in window)) return;
    const io = new IntersectionObserver(
      ([entry]) => setCompact(!entry.isIntersecting),
      { threshold: 0 },
    );
    io.observe(title);
    return () => io.disconnect();
  }, []);

  // Reveal on scroll, once, with per-element stagger via --i.
  useEffect(() => {
    const els = document.querySelectorAll("[data-reveal]");
    if (prefersReducedMotion() || !("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-in");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -6% 0px" },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Scroll-driven effects: hero parallax, statement reveal (monotonic), protocol
  // ribbon, plate registration. One passive listener, one frame per scroll.
  useEffect(() => {
    if (prefersReducedMotion()) {
      document.querySelectorAll("[data-scroll-text]").forEach((el) => el.style.setProperty("--p", 1));
      return;
    }
    const media = mediaRef.current;
    const texts = [...document.querySelectorAll("[data-scroll-text]")].map((el) => ({ el, max: 0 }));
    const plates = [...document.querySelectorAll("[data-plate]")];
    const fine = window.matchMedia("(min-width: 768px)");
    let frame = 0;
    const update = () => {
      frame = 0;
      const y = window.scrollY;
      const vh = window.innerHeight;
      if (media && y < vh * 1.2) {
        media.style.transform = `translate3d(0, ${y * 0.16}px, 0)`;
      }
      texts.forEach((t) => {
        const chapter = t.el.closest(".chapter") || t.el;
        const top = chapter.getBoundingClientRect().top;
        const p = clamp((vh * 0.9 - top) / (vh * 0.6), 0, 1);
        t.max = Math.max(t.max, p);
        t.el.style.setProperty("--p", t.max.toFixed(3));
      });
      const amp = fine.matches ? 7 : 3;
      plates.forEach((plate) => {
        const r = plate.getBoundingClientRect();
        if (r.bottom < -vh || r.top > vh * 2) return;
        const centre = r.top + r.height / 2 - vh / 2;
        const d = clamp(centre / (vh / 2), -1, 1);
        plate.style.setProperty("--reg", (d * amp).toFixed(2));
        plate.style.setProperty("--marks", Math.min(1, Math.abs(d) * 1.6).toFixed(3));
      });
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  // Current chapter marks its nav link, so the header doubles as a position indicator.
  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    const links = [...document.querySelectorAll(".nav-links a")];
    const sections = links.map((a) => document.querySelector(a.getAttribute("href"))).filter(Boolean);
    const hero = document.getElementById("home");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          links.forEach((a) => {
            if (a.getAttribute("href") === `#${entry.target.id}`) a.setAttribute("aria-current", "true");
            else a.removeAttribute("aria-current");
          });
        });
      },
      { rootMargin: "-40% 0px -55% 0px", threshold: 0 },
    );
    sections.forEach((s) => io.observe(s));
    if (hero) io.observe(hero);
    // Header takes the tone of the chapter beneath it.
    const header = document.querySelector(".site-header");
    const inkChapters = [...document.querySelectorAll(".chapter-ink")];
    const tone = new IntersectionObserver(
      (entries) => {
        const over = entries.some((e) => e.isIntersecting) || inkChapters.some((c) => { const r = c.getBoundingClientRect(); return r.top <= 80 && r.bottom >= 80; });
        header?.classList.toggle("is-ink", over);
      },
      { rootMargin: "-80px 0px -100% 0px", threshold: 0 },
    );
    inkChapters.forEach((c) => tone.observe(c));
    const onScroll = () => {
      const over = inkChapters.some((c) => { const r = c.getBoundingClientRect(); return r.top <= 80 && r.bottom >= 80; });
      header?.classList.toggle("is-ink", over);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { io.disconnect(); tone.disconnect(); window.removeEventListener("scroll", onScroll); };
  }, []);

  // Cursor label that follows the pointer inside a project card.
  useEffect(() => {
    const grid = document.querySelector("[data-cursor-grid]");
    if (!grid || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    const move = (e) => {
      const card = e.target.closest(".project-card");
      if (!card) return;
      const r = card.getBoundingClientRect();
      card.style.setProperty("--cx", `${e.clientX - r.left}px`);
      card.style.setProperty("--cy", `${e.clientY - r.top}px`);
    };
    grid.addEventListener("pointermove", move);
    return () => grid.removeEventListener("pointermove", move);
  }, []);

  return (
    <>
      <Seo
        title="Bunny | Blockchain Infrastructure Engineer: Tokenization, Custody, Settlement"
        description="Bunny, Co-Founder & CTO at Sereel. Blockchain infrastructure engineer for institutional tokenization, custody, and private settlement across Bitcoin, EVM, Canton, Solana, Stellar, IOTA, and Monero. Rust, Solidity, DAML, TypeScript."
        image="/og-institutional.png"
        preloadHero
      />
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <Navigation compact={compact} />
      <main id="main">
        <Hero mediaRef={mediaRef} />
        <Work onOpenPaper={() => setPaperOpen(true)} />
        <Networks />
        <Experience />
        <Projects />
        <Journal />
      </main>
      <Contact />
      <Reader paper={paper} open={paperOpen} onClose={() => setPaperOpen(false)} />
    </>
  );
}
